export const currencyWithPrefix = (value: number) => {
    const formattedValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);

    return formattedValue;
};

export const concurrencyWithoutPrefix = (value: number) => {
    const formattedValue = currencyWithPrefix(value);
    return formattedValue.replace(/^R\$\s?/, '');
}

export const currencyToNumber = (value: string) => {
    if (!value) return 0;

    const numericString = value.replace(/[^\d,-]/g, '').replace('.', '').replace(/,/g, '.');
    const numberValue = parseFloat(numericString);

    return isNaN(numberValue) ? 0 : numberValue;
}

// Variante para valores em formato americano (vírgula = milhar, ponto = decimal
// - ex: "66,646.75", "994.727640190"), usada quando a corretora exporta a
// planilha nesse formato (ex: XP) em vez do formato brasileiro assumido por
// currencyToNumber acima. Aplicar a função errada silenciosamente infla o
// valor em ~100x pra qualquer número com casas decimais (ex: "12.70" vira
// 1270 via currencyToNumber) - não são intercambiáveis.
export const currencyToNumberUs = (value: string) => {
    if (!value) return 0;

    const numericString = value.replace(/[^\d.-]/g, '');
    const numberValue = parseFloat(numericString);

    return isNaN(numberValue) ? 0 : numberValue;
}

// Detecta um valor INTEIRO com agrupamento de milhar puro e consistente -
// mesmo separador (só vírgula ou só ponto, nunca os dois misturados) repetido
// em grupos de EXATAMENTE 3 dígitos, ex: "2.787", "1.234.567", "1,234,567".
// Exige o mesmo símbolo em toda a repetição - "1,234.567" (símbolos
// misturados) não bate em nenhum dos dois ramos, cai no fallback ambíguo
// normal (decimal de verdade). O primeiro grupo não pode começar com "0":
// agrupamento de milhar de verdade nunca tem zero à esquerda ("1.234.567",
// nunca "0.234.567") - sem essa exclusão, um decimal genuíno com zero à
// esquerda e 3 casas (ex: "0,128" = 0,128, taxa da Nikos) batia no padrão e
// virava "128" (bug real encontrado pelos testes de regressão da Nikos).
//
// `{min}` controla quantos grupos repetidos são exigidos:
// - parseIntegerAmount (quantidade): 1+ grupo já é suficiente ("2.787" = 2787)
//   porque quantidade nunca tem fração legítima de 3 casas - não há
//   ambiguidade real a proteger.
// - parseAmbiguousDecimal (taxa/PU/financeiro): exige 2+ grupos. Um único
//   grupo (ex: "13,604") é genuinamente ambíguo com um decimal de 3 casas
//   igualmente legítimo (ex: DI Futuro "13,604%" da Nubank, já documentado
//   abaixo) - só fica inequívoco com 2+ grupos repetidos (ex: "1,234,567"),
//   padrão que nenhum campo decimal de verdade usa. Bug real: tratar um único
//   grupo como milhar aqui também fazia "13,604" virar 13604 (achado numa
//   review em cima do fix anterior, que ainda não distinguia 1 grupo de 2+).
const isThousandsGrouped = (value: string, minGroups: 1 | 2): boolean => {
    const dotPattern = new RegExp(`^-?[1-9]\\d{0,2}(\\.\\d{3}){${minGroups},}$`);
    const commaPattern = new RegExp(`^-?[1-9]\\d{0,2}(,\\d{3}){${minGroups},}$`);
    return dotPattern.test(value) || commaPattern.test(value);
};

// Variante pra quando o formato (BR ou US) não é conhecido de antemão e pode
// variar linha a linha - caso da Nikos, onde o número exibido depende da
// configuração regional do Excel de quem copiou (mesma "memória de cálculo"
// mostrou "2,000.00" numa amostra e "1.141.000,00" noutra - confirmado
// olhando o number_format real da célula, que é regional-dependente:
// "#,##0.00"). Heurística: o ÚLTIMO separador (`,` ou `.`) encontrado é o
// decimal; o outro, se houver, é milhar e é descartado. Com um separador só,
// assume decimal (não tenta adivinhar milhar sem o outro símbolo como pista).
//
// Desde 2026-09-09 é o parser padrão de TODAS as corretoras (não só Nikos): a
// suposição fixa de formato por corretora quebrou em produção quando XP e
// PicPay, marcadas como formato americano, mandaram planilha em formato BR -
// "12,00" virou 1200 e o INSERT estourou o NUMERIC(15,6) do banco.
export const parseAmbiguousDecimal = (value: string): number => {
    if (!value) return 0;

    const trimmed = value.trim();

    // Agrupamento de milhar puro com 2+ grupos (ex: "1,234,567", "1.234.567")
    // é inequivocamente um INTEIRO, não um decimal com "último separador =
    // vírgula/ponto decimal" - tratar como decimal dividia o valor por 1000
    // (bug real, review do Copilot em cima de um fix anterior que só cobria
    // parcialmente esse caso). Checar ANTES da heurística de último-separador
    // abaixo. Só com 2+ grupos - ver comentário de isThousandsGrouped acima
    // sobre por que 1 grupo é ambíguo demais pra decidir aqui.
    if (isThousandsGrouped(trimmed, 2)) {
        return parseInt(trimmed.replace(/[.,]/g, ''), 10);
    }

    const lastComma = trimmed.lastIndexOf(',');
    const lastDot = trimmed.lastIndexOf('.');

    let numericString: string;
    if (lastComma === -1 && lastDot === -1) {
        numericString = trimmed.replace(/[^\d-]/g, '');
    } else if (lastComma > lastDot) {
        // vírgula é o decimal (formato BR) - ponto(s) antes dela são milhar e
        // já são removidos pelo replace abaixo. Se sobrar mais de uma vírgula
        // (não bateu no agrupamento puro acima por ter parte decimal real
        // depois, ex: "1,234,56"), vírgulas ANTES da última também são
        // milhar - sem descartá-las, o replace global vira "1.234.56" e
        // parseFloat para no primeiro ponto extra.
        const digitsAndCommas = trimmed.replace(/[^\d,-]/g, '');
        const lastCommaIndex = digitsAndCommas.lastIndexOf(',');
        numericString = (lastCommaIndex === -1
            ? digitsAndCommas
            : digitsAndCommas.slice(0, lastCommaIndex).replace(/,/g, '') + digitsAndCommas.slice(lastCommaIndex)
        ).replace(',', '.');
    } else {
        // ponto é o decimal (formato US) - vírgula(s) antes dele são milhar e
        // já são removidas pelo replace abaixo. Mesmo raciocínio do ramo
        // acima, espelhado pro ponto (ex: "1.234.56").
        const digitsAndDots = trimmed.replace(/[^\d.-]/g, '');
        const lastDotIndex = digitsAndDots.lastIndexOf('.');
        numericString = lastDotIndex === -1
            ? digitsAndDots
            : digitsAndDots.slice(0, lastDotIndex).replace(/\./g, '') + digitsAndDots.slice(lastDotIndex);
    }

    const numberValue = parseFloat(numericString);
    return isNaN(numberValue) ? 0 : numberValue;
}

// Variante da heurística acima para campos INTEIROS (hoje só quantidade). A
// regra "último separador = decimal" está certa pra valor/taxa, mas erraria
// por 1000x em quantidade: "2.787" em formato BR é 2787 unidades, não 2,787.
// Como quantidade fracionária não existe no domínio, aqui um separador
// SOZINHO seguido de exatamente 3 dígitos é tratado como milhar. Essa regra
// NÃO pode valer pros campos decimais: "13,604%" (DI Futuro da Nubank) é
// legitimamente 13,604 e viraria 13604.
export const parseIntegerAmount = (value: string): number => {
    if (!value) return 0;

    const trimmed = value.trim();
    // Todo o valor é um agrupamento de milhar consistente (mesmo separador,
    // repetido em grupos de exatamente 3 dígitos) - cobre tanto um separador só
    // ("2.787") quanto múltiplos grupos ("1.234.567", "1,234,567"). Quantidade
    // não tem fração legítima de 3 casas, então 1 grupo já basta aqui (sem a
    // ambiguidade que existe em parseAmbiguousDecimal).
    if (isThousandsGrouped(trimmed, 1)) {
        return parseInt(trimmed.replace(/[.,]/g, ''), 10);
    }

    // Não bate com o padrão de milhar reconhecido: preserva o valor (sem
    // arredondar) para quem chama rejeitar frações de verdade - quantidade é
    // sempre inteira no domínio. Bug real: Math.round aqui deixava "2,5"/"2.5"
    // virarem 3 e passarem como quantidade válida, sem sinalizar erro nenhum.
    return parseAmbiguousDecimal(trimmed);
};